/**
 * Centralized, value-aware help text for the Constraint Configuration panel.
 *
 * Two levels:
 *   - rowHelp(constraintName)                  -> what the whole rule is
 *   - fieldHelp(field, ctx)                    -> what one editable knob does,
 *                                                 phrased with the row's live values
 *
 * Keeping all copy here (rather than inline in ConstraintsPanel) makes the
 * wording consistent and easy to edit, and lets the field text adapt to each
 * constraint's operation and current numbers.
 *
 * NOTE: this panel edits the *transition constraints* only. The *separation
 * rules* (color / COLOR CHECK gaps) are a separate mechanism that lives only in
 * the YAML, so the help text here intentionally never claims to control them.
 * The same goes for a setup time read from the input file (ROT's
 * setup_from_input_above_h + setup_time_exceptions).
 */

// Human-readable name of the job attribute a constraint compares.
function attrLabel(constraintName) {
  const map = {
    color: 'color',
    design: 'design',
    process_width: 'process width',
    color_check: 'color-check status',
    enddate: 'due date',
  };
  return map[constraintName] || constraintName.replace(/_/g, ' ');
}

// A full clause describing WHEN a rule fires between two consecutive jobs, given
// the comparison operator. Written so it slots cleanly into any sentence frame
// ("... added whenever <clause>", "... fires when <clause>").
function fireClause(operation, attr) {
  switch (operation) {
    case '!=': return `two consecutive jobs have a different ${attr}`;
    case '=':  return `two consecutive jobs have the same ${attr}`;
    case '>':  return `the next job's ${attr} is greater than the previous one's`;
    case '<':  return `the next job's ${attr} is smaller than the previous one's`;
    default:   return `two consecutive jobs differ in ${attr}`;
  }
}

// Plain-language name of the operator (for the Op field help).
function opName(operation) {
  switch (operation) {
    case '!=': return '≠ (Not Equals)';
    case '=':  return '= (Equals)';
    case '>':  return '> (Greater Than)';
    case '<':  return '< (Less Than)';
    default:   return operation;
  }
}

// ---- Row-level help: what each constraint means ---------------------------

const ROW_HELP = {
  color:
    'The Color constraint keeps same-color jobs grouped. When two back-to-back ' +
    'jobs have a different color, the machine pays setup time (Penalty) and the ' +
    'optimizer is charged a soft cost (Effort). Raise the numbers to get fewer ' +
    'color changes.',
  design:
    'The Design constraint keeps same-design jobs grouped. A design change ' +
    'between consecutive jobs costs setup time (Penalty) plus a soft cost ' +
    '(Effort). Raise the numbers to batch jobs of the same design together.',
  process_width:
    'The Process Width constraint expresses a preference about the order of job ' +
    'widths. It fires when the next job is wider (Greater Than), adding a small ' +
    'cost so the optimizer tends to sequence widths smoothly.',
  color_check:
    'The Color Check constraint keeps color-check jobs separate from regular ' +
    'jobs. Switching between a color-check job and a non-color-check job adds a ' +
    'soft cost (Effort), nudging the optimizer to group them.',
  enddate:
    'The Due Date (Enddate) constraint drives the optimizer to finish jobs ' +
    'before their deadline. Lateness is charged per day a job runs past its due ' +
    'date — see the Late/day field. Its small Effort also gently biases jobs ' +
    'toward due-date order.',
};

export function rowHelp(constraintName) {
  return (
    ROW_HELP[constraintName] ||
    `Custom constraint "${constraintName.replace(/_/g, ' ')}". It compares the ` +
    `${attrLabel(constraintName)} of consecutive jobs; when the rule fires the ` +
    `machine pays the Penalty (setup time) and the optimizer is charged the ` +
    `Effort (soft cost).`
  );
}

// ---- Per-constraint field overrides ---------------------------------------
// Some constraints don't fit the generic "transition changeover" wording. These
// override the field text for those cases. Each entry is (field -> fn(ctx)).

const FIELD_OVERRIDES = {
  enddate: {
    penalty: () =>
      'The due-date rule uses no machine setup time, so keep this at 0. ' +
      'Deadline pressure is controlled by the Late/day cost, not by Penalty.',
    effort: ({ effort }) =>
      'A soft bias toward scheduling jobs in due-date order: a small cost is ' +
      'added whenever a job with an earlier due date is placed after one with a ' +
      `later due date. Currently ${effort}. This is a gentle nudge — the real ` +
      'deadline pressure comes from the Late/day cost. Set 0 to ignore ordering.',
    operation: ({ operation }) =>
      `Leave this as ${opName(operation)} for the due-date rule: the optimizer ` +
      "compares each job's due date against the previous job's and adds the soft " +
      'cost when an earlier-due job would run after a later-due one.',
  },
};

// ---- Field-level help: what each knob does, with live values --------------

/**
 * @param {string} field  one of 'penalty' | 'effort' | 'operation' | 'lateness'
 * @param {object} ctx    { constraintName, penalty, effort, operation, latenessPenalty }
 */
export function fieldHelp(field, ctx) {
  const { constraintName, penalty, effort, operation, latenessPenalty } = ctx;

  // Per-constraint override wins when present (e.g. enddate).
  const override = FIELD_OVERRIDES[constraintName] && FIELD_OVERRIDES[constraintName][field];
  if (override) return override(ctx);

  const attr = attrLabel(constraintName);
  const clause = fireClause(operation, attr);

  switch (field) {
    case 'penalty':
      return (
        `Setup time, in hours, added on the machine whenever ${clause}. ` +
        `Currently ${penalty} h. When several constraints fire on the same ` +
        `changeover, the optimizer charges only the largest penalty.`
      );
    case 'effort':
      return (
        `A soft cost added whenever ${clause}. It does NOT consume machine ` +
        `time — it just makes the optimizer avoid that situation. Currently ` +
        `${effort}. Increase it to discourage these transitions; set 0 to ignore it.`
      );
    case 'operation':
      return (
        `How the rule decides it fires when comparing the ${attr} of two ` +
        `back-to-back jobs. Currently ${opName(operation)} — it fires when ` +
        `${clause}.`
      );
    case 'lateness':
      return (
        `Cost charged per day a job finishes after its due date. Currently ` +
        `${latenessPenalty}. Higher values make the optimizer prioritize ` +
        `meeting deadlines (and raise a job's urgency in the manual scheduler).`
      );
    default:
      return '';
  }
}
